import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Pill } from "@/components/ui/Pill";
import { useI18n } from "@/context/I18nContext";
import { useLocalizedNames } from "@/hooks/useLocalizedNames";
import type { ApplicationDetail } from "@/lib/api/review";
import { FileCard } from "./Shared";
import type { MakeUpdateFn, FieldStatus } from "./Shared";
import type { ComplianceCertificate } from "@/lib/api/review";
import type { ApplicationStageStatus } from "@/lib/api/vendor";
import { cn } from "@/lib/utils";

// ─── CertSection ──────────────────────────────────────────────────────────────

interface CertSectionProps {
  labelKey: string;
  cert?: ComplianceCertificate | null;
  certs?: ComplianceCertificate[] | null;
  flex?: boolean;
  fieldStatus?: FieldStatus;
  onMakeUpdate?: () => void;
}

function CertSection({ labelKey, cert, certs, flex, fieldStatus, onMakeUpdate }: CertSectionProps) {
  const { t } = useI18n();
  const hasContent = cert != null || (certs != null && certs.length > 0);

  return (
    <View className={cn("items-start w-full", flex ? "flex-1" : undefined)}>
      <AppText
        weight="bold"
        className="text-base font-sans-medium text-supporting-text uppercase tracking-widest mb-3"
      >
        {labelKey}
      </AppText>
      {!hasContent && (
        <AppText weight="medium" className="text-base text-dark-text-10">
          {t("vendor.application.detail.notUploaded")}
        </AppText>
      )}
      {cert != null && <FileCard url={cert.fileUrl} />}
      {certs != null && certs.map((c, i) => <FileCard key={i} url={c.fileUrl} />)}
      {fieldStatus && hasContent && (
        <View className="flex-row items-center gap-2 pb-2">
          {fieldStatus === "updated" ? (
            <Badge variant="accent-blue" label="vendor.application.detail.fieldUpdated" />
          ) : (
            <>
              <Badge
                variant={fieldStatus === "update_required" ? "filled-warning" : "filled-error"}
                label={
                  fieldStatus === "update_required"
                    ? "vendor.application.detail.fieldUpdateRequired"
                    : "vendor.application.detail.fieldRejected"
                }
              />
              {fieldStatus === "update_required" && (
                <Button
                  variant="primary"
                  label="vendor.application.detail.makeUpdate"
                  onPress={onMakeUpdate ?? (() => {})}
                />
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
}

// ─── Product & Compliance section ─────────────────────────────────────────────

export function ProductComplianceContent({
  isWide,
  stageStatus,
  onMakeUpdate,
  updatedFields,
  application,
}: {
  isWide: boolean;
  stageStatus?: ApplicationStageStatus;
  onMakeUpdate?: MakeUpdateFn;
  updatedFields?: Set<string>;
  application?: ApplicationDetail;
}) {
  const { t } = useI18n();
  const data = application?.productCompliance;
  const { resolveIndustryName, resolveCategoryName } = useLocalizedNames(
    data?.operatingIndustries ?? [],
    data?.operatingCategories ?? []
  );
  const fr = application?.fieldRejections;
  const fs = (key: string): FieldStatus | undefined => {
    if (updatedFields?.has(key)) return "updated";
    return fr?.[key]
      ? stageStatus === "update_required"
        ? "update_required"
        : "rejected"
      : undefined;
  };

  if (!data) return null;

  return (
    <View className={cn("items-start w-full", isWide ? "gap-0" : "px-page-x gap-4")}>
      {/* Row 1: Operating Industry — full width, pills */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-4" : "pt-2")}>
        <AppText weight="medium" className="text-base text-supporting-text mb-3">
          {t("preVetting.step3.industries")}
        </AppText>
        <View className="flex-row flex-wrap gap-2">
          {data.operatingIndustries.map((industry) => (
            <Pill key={industry} variant="tag" plainLabel={resolveIndustryName(industry)} />
          ))}
        </View>
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 2: Product Category — full width, pills */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-4" : "pt-2")}>
        <AppText weight="medium" className="text-base text-supporting-text mb-3">
          {t("preVetting.step3.productSubcategories")}
        </AppText>
        <View className="flex-row flex-wrap gap-2">
          {data.operatingCategories.map((cat) => (
            <Pill key={cat} variant="tag" plainLabel={resolveCategoryName(cat)} />
          ))}
        </View>
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Section header: Compliance & Certifications */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-3" : "pt-1")}>
        <AppText
          weight="bold"
          className="text-base font-sans-bold text-dark-text-15 uppercase tracking-widest"
        >
          {t("preVetting.step3.sectionCompliance")}
        </AppText>
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 3: ISO 9001 + CE Marking — 2-col on desktop */}
      <View
        className={cn("items-start w-full", isWide ? "flex-row gap-6 px-10 py-4" : "pt-2 gap-4")}
      >
        <CertSection
          labelKey={t("preVetting.step3.isoCertification")}
          cert={data.isoCertificate}
          flex={isWide}
          fieldStatus={fs("iso_certification_document")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("iso_certification_document") : undefined}
        />
        {!isWide && <View className="border-b border-stroke-50 w-full" />}
        <CertSection
          labelKey={t("preVetting.step3.ceMarking")}
          cert={data.ceMarking}
          flex={isWide}
          fieldStatus={fs("ce_marking_document")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("ce_marking_document") : undefined}
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 4: Quality Test Reports — full width */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-4" : "pt-2")}>
        <CertSection
          labelKey={t("preVetting.step3.qualityTestReport")}
          certs={data.qualityTestReports}
          fieldStatus={fs("quality_test_report_document")}
          onMakeUpdate={
            onMakeUpdate ? () => onMakeUpdate("quality_test_report_document") : undefined
          }
        />
      </View>
      <View className="border-b border-stroke-50 w-full" />

      {/* Row 5: Eco Certifications — full width, stacked cards */}
      <View className={cn("items-start w-full", isWide ? "px-10 py-4" : "pt-2")}>
        <CertSection
          labelKey={t("preVetting.step3.ecoCertification")}
          certs={data.ecoCertifications}
          fieldStatus={fs("eco_certification_document")}
          onMakeUpdate={onMakeUpdate ? () => onMakeUpdate("eco_certification_document") : undefined}
        />
      </View>
    </View>
  );
}
